import { useStudents } from '@/app/redux/hooks/students';
import { Group } from '@/common/types/group';
import { Student } from '@/common/types/student';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { authenticateStudent } from '@/common/utils/auth';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import CreateAssessmentDialogue from '@/dashboard/Assessment/CreateAssessmentDialogue';
import {
  CaretDownIcon,
  CaretUpDownIcon,
  CaretUpIcon,
  PlusIcon,
  UserCirclePlusIcon,
  UsersFourIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { Checkbox } from '@shadcn/components/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@shadcn/components/select';
import { Skeleton } from '@shadcn/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shadcn/components/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { memo, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import styled from 'styled-components';
import LearnerListRow from './LearnerListRow';
import SelectedLearnerOptions from './SelectedLearnerOptions';

interface AssessmentDialogueState {
  open: boolean;
  initialStudents?: string[];
}

const getSortIcon = (isSorted: false | 'asc' | 'desc') =>
  isSorted === 'asc' ? (
    <CaretUpIcon className="size-3" />
  ) : isSorted === 'desc' ? (
    <CaretDownIcon className="size-3" />
  ) : (
    <CaretUpDownIcon className="size-3" />
  );

const getSortTooltipContent = (
  column: string,
  isSorted: false | 'asc' | 'desc',
) => {
  switch (column) {
    case 'name':
      if (!isSorted) {
        return 'Sort by name A to Z';
      }
      if (isSorted === 'asc') {
        return 'Currently A to Z (click for Z to A)';
      }
      return 'Currently Z to A (click for A to Z)';
    case 'lastLogin':
      if (isSorted === 'asc') {
        return 'Currently most recent first (click for oldest first)';
      }
      return 'Currently oldest first (click for most recent first)';
    case 'level':
      if (isSorted === 'asc') {
        return 'Currently lowest level first (click for highest level first)';
      }
      return 'Currently highest level first (click for lowest level first)';
  }
};

const SortableHeader = ({
  id,
  name,
  isSorted,
  onClick,
}: {
  id: string;
  name: string;
  isSorted: false | 'asc' | 'desc';
  onClick?: (e: unknown) => void;
}) => (
  <Tooltip>
    <TooltipTrigger>
      <div
        className="flex items-center gap-2 hover:bg-sidebar-accent px-2 py-1 my-1 rounded-md cursor-pointer truncate w-fit"
        onClick={onClick}
      >
        <span>{name}</span>
        {getSortIcon(isSorted)}
      </div>
    </TooltipTrigger>
    <TooltipContent>{getSortTooltipContent(id, isSorted)}</TooltipContent>
  </Tooltip>
);

const columnHelper = createColumnHelper<Student>();

interface LearnerListProps {
  students: Student[];
  group?: Group;
  onAddLearner?: () => void;
  onBulkAdd?: () => void;
  onAddLearnersToGroup?: () => void;
  hideAddLearnerCta?: boolean;
}
export default function LearnerList({
  students,
  group,
  onAddLearner,
  onBulkAdd,
  onAddLearnersToGroup,
  hideAddLearnerCta = false,
}: LearnerListProps) {
  const navigate = useNavigate();

  const { isLoading, isFetching } = useStudents();

  const [sortingState, setSortingState] = useState<SortingState>([
    { id: 'name', desc: false },
  ]);
  const [nameSortMode, setNameSortMode] = useState<'firstName' | 'lastName'>(
    'lastName',
  );
  const [rowSelectionState, setRowSelectionState] = useState<
    Record<string, boolean>
  >({});
  const [assessmentDialogueState, setAssessmentDialogueState] =
    useState<AssessmentDialogueState>({
      open: false,
      initialStudents: [],
    });

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        size: 48,
        header: ({ table }) => (
          <Checkbox
            className="ml-2"
            checked={
              table.getIsSomeRowsSelected()
                ? 'indeterminate'
                : table.getIsAllRowsSelected()
            }
            onCheckedChange={() => table.toggleAllRowsSelected()}
          />
        ),
      }),
      columnHelper.accessor(
        (row) => `${row.studentFirstName} ${row.studentSurname}`,
        {
          id: 'name',
          size: 192,
          header: ({ header: _header, column: _column }) => (
            <Select
              value={
                !sortingState[0] || sortingState[0]?.id !== 'name'
                  ? undefined
                  : `${nameSortMode}-${sortingState[0]?.desc ? 'desc' : 'asc'}`
              }
              onValueChange={(value) => {
                const [mode, dir] = value.split('-') as [
                  'firstName' | 'lastName',
                  'asc' | 'desc',
                ];
                setNameSortMode(mode);
                setSortingState([{ id: 'name', desc: dir === 'desc' }]);
              }}
            >
              <SelectTrigger asChild>
                <div className="flex items-center gap-2 hover:bg-sidebar-accent px-2 py-1 my-1 rounded-md cursor-pointer truncate w-fit">
                  <span>Name/Username</span>
                  {getSortIcon(
                    sortingState[0]?.id === 'name'
                      ? sortingState[0].desc
                        ? 'desc'
                        : 'asc'
                      : false,
                  )}
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="firstName-asc">
                  A → Z (first name)
                </SelectItem>
                <SelectItem value="firstName-desc">
                  Z → A (first name)
                </SelectItem>
                <SelectItem value="lastName-asc">A → Z (last name)</SelectItem>
                <SelectItem value="lastName-desc">Z → A (last name)</SelectItem>
              </SelectContent>
            </Select>
          ),
          sortingFn: (rowA, rowB) => {
            const aPrimary =
              nameSortMode === 'firstName'
                ? rowA.original.studentFirstName
                : rowA.original.studentSurname;
            const bPrimary =
              nameSortMode === 'firstName'
                ? rowB.original.studentFirstName
                : rowB.original.studentSurname;

            const primaryCompare = aPrimary.localeCompare(bPrimary, undefined, {
              sensitivity: 'base',
            });
            if (primaryCompare !== 0) return primaryCompare;

            const aSecondary =
              nameSortMode === 'firstName'
                ? rowA.original.studentSurname
                : rowA.original.studentFirstName;
            const bSecondary =
              nameSortMode === 'firstName'
                ? rowB.original.studentSurname
                : rowB.original.studentFirstName;

            return aSecondary.localeCompare(bSecondary, undefined, {
              sensitivity: 'base',
            });
          },
        },
      ),
      columnHelper.accessor('lastLogin', {
        size: 128,
        header: ({ header, column }) => (
          <SortableHeader
            id={header.id}
            name="Last Login"
            isSorted={column.getIsSorted()}
            onClick={column.getToggleSortingHandler()}
          />
        ),
      }),
      columnHelper.accessor('level', {
        size: 430,
        header: ({ header, column }) => (
          <SortableHeader
            id={header.id}
            name="Level"
            isSorted={column.getIsSorted()}
            onClick={column.getToggleSortingHandler()}
          />
        ),
      }),
      columnHelper.display({ id: 'groups', size: 80, header: 'Groups' }),
      columnHelper.display({ id: 'actions', size: 100 }),
    ],
    [nameSortMode, sortingState],
  );

  const table = useReactTable({
    data: students,
    getRowId: (row) => row.id,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSortingState,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelectionState,
    state: { sorting: sortingState, rowSelection: rowSelectionState },
  });

  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);

  const rows = table.getRowModel().rows;

  const selectedStudentIds = useMemo(
    () => new Set(Object.keys(rowSelectionState)),
    [rowSelectionState],
  );

  const handleCreateAssessment = useCallback((studentId: string) => {
    trackMixpanelEvent('Create Assessment clicked from Learner List Row');
    setAssessmentDialogueState({
      open: true,
      initialStudents: [studentId],
    });
  }, []);

  const handleToggleSelected = useCallback(
    (studentId: string) => {
      const row = table.getRow(studentId);
      row.toggleSelected();
    },
    [table],
  );

  const handleLearnerLogin = useCallback(
    async (studentId: string) => {
      try {
        const row = table.getRow(studentId);
        const student = row.original;
        await authenticateStudent(student.spellcastUsername, student.password);
        navigate('/game');
      } catch (e) {
        console.error(e);
        toast.error('Failed to authenticate student', {
          description: UNKNOWN_ERROR_MESSAGE,
        });
      }
    },
    [table, navigate],
  );

  return (
    <LearnerListContainer ref={setScrollEl}>
      <Table style={{ tableLayout: 'fixed' }}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} style={{ width: header.getSize() }}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading || isFetching ? (
            [...Array(20)].map((_, i) => (
              <TableRow key={i}>
                {[...Array(6)].map((_, j) => (
                  <TableCell key={j}>
                    <Skeleton className="w-full h-7" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : rows.length > 0 ? (
            <VirtualRows
              rows={rows}
              scrollElement={scrollEl}
              group={group}
              rowSelectionState={rowSelectionState}
              handleToggleSelected={handleToggleSelected}
              handleLearnerLogin={handleLearnerLogin}
              handleCreateAssessment={handleCreateAssessment}
            />
          ) : !group && !hideAddLearnerCta ? (
            <TableRow className="hover:bg-white">
              <TableCell colSpan={7}>
                <div className="w-full min-h-[70vh] flex flex-col items-center justify-center py-12 gap-6">
                  <UserCirclePlusIcon
                    size={80}
                    className="text-neutral-400"
                    weight="thin"
                  />
                  <div className="flex flex-col items-center gap-2">
                    <h3 className="text-lg font-medium text-neutral-900">
                      Add learners to get started
                    </h3>
                    <p className="text-sm text-neutral-500 max-w-[400px] text-center">
                      Add learners individually or in bulk. You can organise
                      learners into groups for easier management.
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Button className="gap-2" onClick={onAddLearner}>
                      <PlusIcon size={20} />
                      Add learner
                    </Button>
                    <Button
                      className="gap-2"
                      variant="outline"
                      onClick={onBulkAdd}
                    >
                      <UsersFourIcon size={20} />
                      Bulk add learners
                    </Button>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          ) : onAddLearnersToGroup ? (
            <TableRow className="hover:bg-white">
              <TableCell colSpan={7}>
                <div className="w-full min-h-[70vh] flex flex-col items-center justify-center py-12 gap-6">
                  <div className="w-full min-h-[70vh] flex flex-col items-center justify-center py-12 gap-6">
                    <UserCirclePlusIcon
                      size={80}
                      className="text-neutral-400"
                      weight="thin"
                    />
                    <div className="flex flex-col items-center gap-2">
                      <h3 className="text-lg font-medium text-neutral-900">
                        Add learners to this group to get started
                      </h3>
                    </div>
                    <Button variant="outline" onClick={onAddLearnersToGroup}>
                      <UsersFourIcon size={20} className="mr-2" />
                      Add learners to group
                    </Button>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            <TableRow className="hover:bg-white text-muted-foreground">
              <TableCell colSpan={7}>
                <div className="w-full flex flex-col items-center justify-center py-12 gap-6">
                  <p>No learners found</p>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {selectedStudentIds.size > 0 && (
        <SelectedLearnerOptions
          selectedStudentIds={selectedStudentIds}
          group={group}
          onClose={() => table.resetRowSelection()}
        />
      )}

      <CreateAssessmentDialogue
        isOpen={assessmentDialogueState.open}
        onClose={() =>
          setAssessmentDialogueState({
            ...assessmentDialogueState,
            open: false,
          })
        }
        initialStudents={assessmentDialogueState.initialStudents}
      />
    </LearnerListContainer>
  );
}

interface VirtualRowsProps {
  rows: Row<Student>[];
  scrollElement: HTMLDivElement | null;
  group?: Group;
  rowSelectionState: Record<string, boolean>;
  handleToggleSelected: (studentId: string) => void;
  handleLearnerLogin: (studentId: string) => void;
  handleCreateAssessment: (studentId: string) => void;
}

const VirtualRows = memo(function VirtualRows({
  rows,
  scrollElement,
  group,
  rowSelectionState,
  handleToggleSelected,
  handleLearnerLogin,
  handleCreateAssessment,
}: VirtualRowsProps) {
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => 65,
    overscan: 30,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? virtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
      : 0;

  return (
    <>
      {paddingTop > 0 && (
        <TableRow>
          <TableCell colSpan={6} style={{ height: paddingTop, padding: 0 }} />
        </TableRow>
      )}
      {virtualItems.map((virtualRow) => {
        const row = rows[virtualRow.index];
        return (
          <LearnerListRow
            key={row.id}
            student={row.original}
            selected={rowSelectionState[row.id] ?? false}
            onSelectedChange={handleToggleSelected}
            handleLearnerLogin={handleLearnerLogin}
            handleCreateAssessment={handleCreateAssessment}
            group={group}
            isScrolling={virtualizer.isScrolling}
          />
        );
      })}
      {paddingBottom > 0 && (
        <TableRow>
          <TableCell
            colSpan={6}
            style={{ height: paddingBottom, padding: 0 }}
          />
        </TableRow>
      )}
    </>
  );
});

const LearnerListContainer = styled.div`
  height: 100%;
  overflow-y: auto;
`;
